(() => {
  'use strict';

  const instances = new WeakMap();

  const safeUrl = (value) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const parsed = new URL(value, window.location.origin);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
      return parsed.href;
    } catch (error) {
      return '';
    }
  };

  class CahveReviewWall {
    constructor(root) {
      this.root = root;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.items = Array.from(root.querySelectorAll('[data-crw-item]'));
      this.openButtons = Array.from(root.querySelectorAll('[data-crw-open-lightbox]'));

      this.lightbox = root.querySelector('[data-crw-lightbox]');
      this.lightboxDialog = root.querySelector('[data-crw-lightbox-dialog]');
      this.lightboxImage = root.querySelector('[data-crw-lightbox-image]');
      this.lightboxName = root.querySelector('[data-crw-lightbox-name]');
      this.lightboxShopLink = root.querySelector('[data-crw-lightbox-shop-link]');
      this.lightboxShopLabel = root.querySelector('[data-crw-lightbox-shop-label]');
      this.lightboxShopLabelClone = root.querySelector('[data-crw-lightbox-shop-label-clone]');
      this.lightboxPrev = root.querySelector('[data-crw-lightbox-previous]');
      this.lightboxNext = root.querySelector('[data-crw-lightbox-next]');
      this.lightboxCloseTargets = Array.from(root.querySelectorAll('[data-crw-lightbox-close]'));

      this.reviews = this.readReviews();
      this.activeIndex = 0;
      this.lastFocused = null;

      this.onKeydown = this.onKeydown.bind(this);

      this.bind();
      this.observeHeaderReveal();
      this.observeItemReveal();
    }

    readReviews() {
      const node = this.root.querySelector('[data-crw-data]');
      if (!node) return [];
      try {
        const parsed = JSON.parse(node.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    bind() {
      this.openButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const index = Number(button.dataset.index);
          this.openLightbox(Number.isInteger(index) ? index : 0, button);
        });
      });

      this.lightboxCloseTargets.forEach((target) => {
        target.addEventListener('click', () => this.closeLightbox());
      });

      this.lightbox?.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.closeLightbox();
      });

      this.lightboxPrev?.addEventListener('click', () => this.stepLightbox(-1));
      this.lightboxNext?.addEventListener('click', () => this.stepLightbox(1));

      document.addEventListener('keydown', this.onKeydown);
    }

    observeHeaderReveal() {
      if (this.reducedMotion.matches || !('IntersectionObserver' in window)) {
        this.root.classList.add('is-visible');
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.root.classList.add('is-visible');
        observer.disconnect();
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      observer.observe(this.root);
    }

    observeItemReveal() {
      if (this.root.dataset.reveal !== 'true' || this.reducedMotion.matches || !('IntersectionObserver' in window)) {
        this.items.forEach((item) => item.classList.add('is-visible'));
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 });
      this.items.forEach((item) => observer.observe(item));
    }

    openLightbox(index, triggerButton) {
      if (!this.lightbox || !this.reviews.length) return;
      this.lastFocused = triggerButton || document.activeElement;
      this.activeIndex = Math.min(Math.max(index, 0), this.reviews.length - 1);
      this.renderLightbox();
      this.lightbox.hidden = false;
      document.body.classList.add('cahve-review-lightbox-open');
      requestAnimationFrame(() => this.lightboxDialog?.focus());
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;
      this.lightbox.hidden = true;
      document.body.classList.remove('cahve-review-lightbox-open');
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    stepLightbox(direction) {
      if (!this.reviews.length) return;
      this.activeIndex = (this.activeIndex + direction + this.reviews.length) % this.reviews.length;
      this.renderLightbox();
    }

    renderLightbox() {
      const review = this.reviews[this.activeIndex];
      if (!review || !this.lightboxImage) return;

      const imageUrl = safeUrl(review.image);
      this.lightboxImage.src = imageUrl;
      this.lightboxImage.alt = review.alt || 'Customer review screenshot';

      if (this.lightboxName) {
        if (review.name) {
          this.lightboxName.textContent = review.name;
          this.lightboxName.hidden = false;
        } else {
          this.lightboxName.hidden = true;
          this.lightboxName.textContent = '';
        }
      }

      const productUrl = safeUrl(review.productUrl);
      if (this.lightboxShopLink) {
        if (productUrl && review.productTitle) {
          const label = `Shop ${review.productTitle}`;
          this.lightboxShopLink.href = productUrl;
          this.lightboxShopLink.hidden = false;
          if (this.lightboxShopLabel) this.lightboxShopLabel.textContent = label;
          if (this.lightboxShopLabelClone) this.lightboxShopLabelClone.textContent = label;
        } else {
          this.lightboxShopLink.hidden = true;
          this.lightboxShopLink.removeAttribute('href');
        }
      }
    }

    onKeydown(event) {
      if (!this.lightbox || this.lightbox.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeLightbox();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.stepLightbox(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.stepLightbox(1);
      }
    }
  }

  const init = (root) => {
    if (!root || instances.has(root)) return;
    instances.set(root, new CahveReviewWall(root));
  };

  const initAll = (scope) => {
    (scope || document).querySelectorAll('.cahve-review-wall').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
